import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Users, Sparkles, MessageCircle } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1707451811841-0f881783cde8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxwZW9wbGUlMjB3b3Jrc2hvcCUyMGZvcmVzdCUyMGdhdGhlcmluZ3xlbnwwfHx8fDE3NzgwNjA3MjF8MA&ixlib=rb-4.1.0&q=85";
const COMM_IMG = "https://images.unsplash.com/photo-1722587511207-67e59383ffbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxjb21tdW5pdHklMjBnYXJkZW4lMjBwZW9wbGUlMjBuYXR1cmV8ZW58MHx8fHwxNzc4MDYwNzIwfDA&ixlib=rb-4.1.0&q=85";
const ARCH_IMG = "https://images.unsplash.com/photo-1556983852-43bf21186b2a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxzdXN0YWluYWJsZSUyMGFyY2hpdGVjdHVyZSUyMG5hdHVyZXxlbnwwfHx8fDE3NzgwNjA3Mzl8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-24 md:pt-28 md:pb-32 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-7 fade-in">
            <div className="text-xs tracking-[0.3em] uppercase text-tc-secondary mb-8" data-testid="hero-eyebrow">
              Un manifesto / una piattaforma
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-tc-primary leading-[0.95] mb-8">
              Non siamo individui.<br/>
              <em className="text-tc-secondary italic">Siamo radice.</em>
            </h1>
            <p className="text-lg md:text-xl text-tc-muted max-w-xl leading-relaxed mb-10 font-light">
              Terra Comune raduna progetti, realtà, eventi e persone che stanno scrivendo una nuova storia —
              non in contrapposizione al mondo che conosciamo, ma a fianco della Terra, come parte di essa.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/realta" className="btn-primary inline-flex items-center gap-2" data-testid="cta-explore">
                Esplora le realtà <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/consigli" className="btn-outline" data-testid="cta-advice">Ricevi un consiglio</Link>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-tc-secondary/20 blur-2xl" />
            <img src={HERO_IMG} alt="Persone nel bosco" className="relative rounded-[32px] w-full h-[520px] object-cover shadow-2xl" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full bg-tc-accent/30 blur-2xl" />
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-tc-surface py-24 md:py-32 relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="text-xs tracking-[0.3em] uppercase text-tc-secondary mb-10">Il manifesto</div>
          <p className="font-serif text-3xl md:text-5xl text-tc-primary leading-tight italic">
            "Ricominciare da zero non significa distruggere.<br/>
            Significa tornare alla radice — dove la Terra e noi<br/>
            non siamo mai stati separati."
          </p>
          <p className="mt-10 text-sm text-tc-muted tracking-widest">— ispirato da scienziati e studiosi di vite alternative</p>
        </div>
      </section>

      {/* FOUR PILLARS - asymmetric bento */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-6">
          <PillarCard
            to="/realta"
            title="Realtà sul territorio"
            desc="Ecovillaggi, reti olistiche, progetti rigenerativi. Una mappa viva."
            icon={<MapPin className="w-6 h-6" />}
            img={ARCH_IMG}
            className="md:col-span-7 md:row-span-2 h-[420px]"
            testid="pillar-realities"
          />
          <PillarCard
            to="/eventi"
            title="Eventi e laboratori"
            desc="Ritiri, festival, formazioni, incontri aperti."
            icon={<Calendar className="w-6 h-6" />}
            className="md:col-span-5 h-[200px]"
            testid="pillar-events"
          />
          <PillarCard
            to="/comunita"
            title="Comunità"
            desc="Persone che camminano nella stessa direzione."
            icon={<Users className="w-6 h-6" />}
            img={COMM_IMG}
            className="md:col-span-5 h-[200px]"
            testid="pillar-community"
          />
          <PillarCard
            to="/forum"
            title="Forum"
            desc="Domande, idee, esperienze, scambi. La voce comune."
            icon={<MessageCircle className="w-6 h-6" />}
            className="md:col-span-6 h-[180px]"
            testid="pillar-forum"
          />
          <PillarCard
            to="/consigli"
            title="Consigli e tecniche"
            desc="Articoli e Radice, una guida AI gentile."
            icon={<Sparkles className="w-6 h-6" />}
            className="md:col-span-6 h-[180px]"
            testid="pillar-advice"
          />
        </div>
      </section>

      {/* QUOTE BLOCK */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 text-center">
        <p className="font-serif text-2xl md:text-3xl text-tc-accent italic leading-snug">
          "La vera evoluzione comincerà il giorno in cui smetteremo di pensarci
          come individui separati e cominceremo a sentirci come parte di un tutto."
        </p>
      </section>
    </main>
  );
}

function PillarCard({ to, title, desc, icon, img, className = "", testid, wide = false }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group relative overflow-hidden rounded-3xl border border-tc/70 bg-tc-surface p-8 flex ${wide ? 'flex-row justify-between items-center' : 'flex-col justify-between'} hover:-translate-y-1 transition-all duration-500 ${className}`}
      style={img ? { backgroundImage: `linear-gradient(180deg, rgba(242,235,225,0.4) 0%, rgba(45,74,34,0.85) 100%), url(${img})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
    >
      <div className={img ? "text-tc-bg" : "text-tc-primary"}>
        <div className="flex items-center gap-3">
          {icon}
          <h3 className={`font-serif text-3xl md:text-4xl ${img ? 'text-tc-bg' : 'text-tc-primary'}`}>{title}</h3>
        </div>
        <p className={`mt-3 max-w-md text-sm md:text-base ${img ? 'text-tc-bg/90' : 'text-tc-muted'}`}>{desc}</p>
      </div>
      <ArrowRight className={`${img ? 'text-tc-bg' : 'text-tc-primary'} w-6 h-6 self-end group-hover:translate-x-2 transition-transform`} />
    </Link>
  );
}
